// App.js
import React, { useState, useEffect } from "react";
import { Amplify } from "aws-amplify";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import awsConfig from "./aws-config";
import "./App.css";

import HomePage from "./pages/HomePage";
import RecruiterHomePage from "./pages/RecruiterHomePage";
import AuthPage from "./pages/AuthPage";
import RecruitmentOnboarding from "./pages/RecruitmentOnboarding";
import SettingsPage from "./pages/SettingsPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import IntegrationsDirectoryPage from "./pages/IntegrationsDirectoryPage";
import QRPage from "./pages/QRPage";
import PublicMenuPage from "./pages/PublicMenuPage";
import SuccessPage from "./pages/SuccessPage";
import CancelPage from "./pages/CancelPage";
import SystemGeneratorPage from "./pages/SystemGeneratorPage";
import RecruitmentBuilderPage from "./pages/RecruitmentBuilderPage";
import Rec2RecSystemsPage from "./pages/Rec2RecSystemsPage";
import RestaurantSystemsPage from "./pages/RestaurantSystemsPage";
import UsedCarDecisionPage from "./pages/UsedCarDecisionPage";
import FitnessBookingTemplatePage from "./pages/FitnessBookingTemplatePage";
import GatecheckTemplatePage from "./pages/GatecheckTemplatePage";
import CollaborationMilestoneGatecheckPage from "./pages/CollaborationMilestoneGatecheckPage";
import { fetchBusiness as fetchBusinessFromAPI } from "./api/business";

Amplify.configure(awsConfig);

// --- Error boundary ---
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err, info) {
    console.error("App crashed:", err, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" style={{ padding: 24 }}>
          Something went wrong. Please refresh.
        </div>
      );
    }
    return this.props.children;
  }
}

// --- Protect routes ---
function ProtectedRoute({ user, children }) {
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

export default function App() {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [business, setBusiness] = useState(() => {
    try {
      const stored = localStorage.getItem("business");
      return stored ? JSON.parse(stored) : undefined; // undefined = not loaded yet
    } catch {
      return undefined;
    }
  });

  // persist user
  useEffect(() => {
    try {
      if (user) localStorage.setItem("user", JSON.stringify(user));
      else localStorage.removeItem("user");
    } catch {}
  }, [user]);

  // fetch business once user logs in
  useEffect(() => {
    if (!user) {
      setBusiness(undefined);
      return;
    }
    let mounted = true;
    const loadBusiness = async () => {
      const biz = await fetchBusinessFromAPI();
      if (!mounted) return;
      if (biz === undefined) {
        setBusiness((prev) => (prev === undefined ? null : prev));
        return;
      }
      setBusiness(biz);
      try {
        if (biz) localStorage.setItem("business", JSON.stringify(biz));
        else localStorage.removeItem("business");
      } catch {}
    };
    loadBusiness();
    return () => {
      mounted = false;
    };
  }, [user]);

  const handleOnboardingComplete = (data) => {
    const lines =
      Array.isArray(data?.addressLines) && data.addressLines.length
        ? data.addressLines
        : [
            data?.addressLine1,
            data?.addressLine2,
            data?.addressCity,
            data?.addressPostcode,
            data?.addressCountry,
          ].filter((part) => part && String(part).trim().length > 0);
    const newBiz = {
      businessId: data?.businessId,
      name: data?.name || "Your Kitchen",
      slug: data?.slug || "your-kitchen",
      logoSet: Boolean(data?.logo),
      hasMenu: Array.isArray(data?.menuItems)
        ? data.menuItems.length > 0
        : false,
      hoursSet: Boolean(data?.hours),
      customDomain: data?.customDomain ?? null,
      phone: data?.phone || "",
      email: data?.email || "",
      businessAddress: {
        street: data?.addressLine1 || "",
        line2: data?.addressLine2 || "",
        city: data?.addressCity || "",
        postcode: data?.addressPostcode || "",
        country: data?.addressCountry || "",
      },
      businessAddressLines: lines,
      businessAddressFormatted: lines.join(", "),
      onboardingComplete: true,
    };
    setBusiness(newBiz);
    try {
      localStorage.setItem("business", JSON.stringify(newBiz));
      localStorage.setItem("seenOnboarding", "true");
      if (data?.businessId) localStorage.setItem("businessId", data.businessId);
    } catch {}
  };

  const handleSignOut = () => {
    setUser(null);
    setBusiness(undefined);
    try {
      localStorage.removeItem("user");
      localStorage.removeItem("business");
      localStorage.removeItem("seenOnboarding");
      localStorage.removeItem("businessId");
      localStorage.removeItem("onboardingFormData");
      localStorage.removeItem("onboardingStep");
    } catch {}
  };

  return (
    <AppErrorBoundary>
      <div className="app-shell">
        <Router>
          <Routes>

          {/* Stripe payment success/cancel routes */}
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/cancel" element={<CancelPage />} />

          {/* Public ordering page */}
          <Route path="/:businessId" element={<PublicMenuPage />} />

          {/* Auth */}
          <Route path="/auth" element={<AuthPage onAuthSuccess={setUser} />} />

          {/* Natural-language generator */}
          <Route
            path="/systems"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <SystemGeneratorPage business={business} />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/systems/recruitment"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <RecruitmentBuilderPage business={business} />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/systems/rec2rec"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <Rec2RecSystemsPage business={business} />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/systems/restaurant"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <RestaurantSystemsPage business={business} />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/systems/used-car"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <UsedCarDecisionPage business={business} />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/systems/fitness-booking"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <FitnessBookingTemplatePage business={business} />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/systems/gatecheck"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <GatecheckTemplatePage business={business} />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/systems/collaboration-milestone-gatecheck"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <CollaborationMilestoneGatecheckPage business={business} />
                )}
              </ProtectedRoute>
            }
          />

          {/* Recruiter dashboard (protected) */}
          <Route
            path="/"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  // still loading → show nothing/spinner
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business ? (
                  <Navigate to="/onboarding" replace />
                ) : business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <RecruiterHomePage
                    user={user}
                    business={business}
                    onSignOut={handleSignOut}
                  />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/home"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business ? (
                  <Navigate to="/onboarding" replace />
                ) : business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <HomePage
                    user={user}
                    business={business}
                    onSignOut={handleSignOut}
                  />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business ? (
                  <Navigate to="/onboarding" replace />
                ) : business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <RecruiterHomePage
                    user={user}
                    business={business}
                    onSignOut={handleSignOut}
                  />
                )}
              </ProtectedRoute>
            }
          />

          {/* Onboarding */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute user={user}>
                {business && business.onboardingComplete !== false ? (
                  <Navigate to="/recruiter" replace />
                ) : (
                  <RecruitmentOnboarding onComplete={handleOnboardingComplete} />
                )}
              </ProtectedRoute>
            }
          />


          {/* Menu deprecated: redirect to Systems */}
          <Route path="/menu/*" element={<Navigate to="/systems" replace />} />

          {/* Settings (protected, onboarding check) */}
          <Route
            path="/settings/*"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <SettingsPage user={user} business={business} />
                )}
              </ProtectedRoute>
            }
          />

          {/* QR (protected, onboarding check) */}
          <Route
            path="/qr"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <QRPage user={user} business={business} />
                )}
              </ProtectedRoute>
            }
          />

          {/* Integrations directory */}
          <Route
            path="/integrations"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <IntegrationsDirectoryPage />
                )}
              </ProtectedRoute>
            }
          />

          {/* WordPress integration detail */}
          <Route
            path="/integrations/wordpress"
            element={
              <ProtectedRoute user={user}>
                {business === undefined ? (
                  <div style={{ padding: 32 }}>Loading business…</div>
                ) : !business || business.onboardingComplete === false ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <IntegrationsPage />
                )}
              </ProtectedRoute>
            }
          />

            {/* 404 fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </div>
    </AppErrorBoundary>
  );
}
